<template>
  <article class="group">
    <header class="group-row group-row--header">
      <h3 class="h5 mrgv0 text-truncate">
        <div v-html="highlight(group.name)" />
      </h3>

      <aside>
        <time
          v-if="group.start_datetime"
          :datetime="group.start_datetime"
          class="group-row__time"
        >
          <span v-if="!group.end_datetime" class="text-small text-alt">
            {{ $t('planification.groups.fields.start-datetime.label') }}
          </span>
          {{ group.start_datetime && formatDate(group.start_datetime) }}
        </time>

        <time
          v-if="group.end_datetime"
          :datetime="group.end_datetime"
          class="group-row__time"
        >
          <span v-if="!group.start_datetime" class="text-small text-alt">
            {{ $t('planification.groups.fields.end-datetime.label') }}
          </span>
          {{ group.end_datetime && formatDate(group.end_datetime) }}
        </time>
      </aside>

      <ListInline class="justify-content-end">
        <li class="group-row__action">
          <Btn
            v-if="directory != null"
            variant="link"
            class="pdgh0"
            @click="redirectToDirectory"
          >
            <Icon id="phone-book" :scale="0.6" />
            {{ $t('planification.groups.linked-directory') }}
          </Btn>
        </li>
        <li class="group-row__action">
          <Btn variant="link" class="pdgh0" @click="toggleGroupUpdate">
            <Icon id="edit" :scale="0.6" />
            {{ $t('common.edit') }}
          </Btn>
        </li>
        <li class="group-row__action">
          <Btn variant="link" class="pdgh0" @click="toggleGroupDelete">
            <Icon id="trash" :scale="0.6" />
            {{ $t('common.delete') }}
          </Btn>
        </li>
        <li>
          <Btn variant="bare" @click="toggleOpen = !toggleOpen">
            <Icon
              :id="toggleOpen ? 'chevron-down' : 'chevron-up'"
              :scale="0.5"
            />
          </Btn>
        </li>
      </ListInline>
    </header>

    <CToggle v-model="toggleOpen" class="group-content">
      <CToggleContent>
        <ListStacked v-if="users && users.length" size="tight">
          <li v-for="user in users" :key="user.id">
            <PlanificationUser :group="group" :user="user" />
          </li>
        </ListStacked>

        <div v-if="!users.length && !subgroups.length" class="group-row">
          <div class="text-alt pdgv--">
            <Icon id="alert" :scale="0.75" class="mrgr--" />
            {{ $t('planification.users.no-users') }}
          </div>
        </div>

        <footer class="group-footer">
          <Btn variant="link" @click="toggleUserAdd">
            <Icon id="add" :scale="0.6" />
            {{ $t('planification.users.add.title') }}
          </Btn>
        </footer>

        <ListStacked
          v-if="subgroups && subgroups.length"
          size="tight"
          class="group-groups"
        >
          <li v-for="(subgroup, index) in subgroups" :key="index">
            <PlanificationGroup :group="subgroup" />
          </li>
        </ListStacked>
      </CToggleContent>
    </CToggle>
  </article>
</template>

<script>
import eventBus from '@/eventBus';
import { formatDate } from '@/helpers/dates';

import PlanificationUser from '@/components/planification/PlanificationUser';

export default {
  name: 'PlanificationGroup',

  components: {
    PlanificationUser,
  },

  props: {
    group: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      toggleOpen: true,
    };
  },

  computed: {
    directory() {
      return this.group?.configuration?.directory;
    },

    members() {
      return this.group?.members?.length
        ? this.group.members.map(member => {
            const user = this.$store.getters['users/getUserByUsername'](
              member.username
            );
            member.user = user;

            return member;
          })
        : [];
    },

    subgroups() {
      return this.members.length
        ? this.members
            .filter(member => !member.user)
            .map(group => {
              const fullGroup = this.$store.getters[
                'planification/getGroupByUsername'
              ](group.username);

              return {
                ...group,
                ...fullGroup,
              };
            })
        : [];
    },

    users() {
      return this.members.length
        ? this.members.filter(member => member.user)
        : [];
    },
  },

  methods: {
    highlight(text) {
      return text
        ? text.replace(
            new RegExp(this.$store.state.planification.sort.groupName, 'gi'),
            match => `<mark>${match}</mark>`
          )
        : text;
    },

    formatDate(date) {
      return formatDate(date, 'dd.MM.yyyy HH:mm');
    },

    redirectToDirectory() {
      this.$router.push(`/directories/${this.group.configuration.directory}`);
    },

    toggleGroupUpdate() {
      eventBus.$emit('togglePlanificationGroupAddOrUpdate', this.group);
    },

    toggleGroupDelete() {
      eventBus.$emit('togglePlanificationGroupDelete', this.group);
    },

    toggleUserAdd() {
      eventBus.$emit('togglePlanificationUserAddOrUpdate', {
        group: this.group,
      });
    },
  },
};
</script>

<style lang="scss">
.group-row {
  margin-top: -1px;
  padding: 0.5rem 0.75rem;

  display: grid;
  grid-template-columns: 2fr auto 2fr;
  gap: 1rem;
  align-items: center;

  border: 1px solid $alt-100;
}

.group-row__time {
  font-size: rem(13px);
  color: $alt-500;

  &:not(:first-child) {
    margin-left: 1rem;
  }
}

.group-row__action {
  font-size: rem(13px);

  opacity: 0;
  transition: opacity 0.15s ease-in;

  :hover > &,
  :focus-within > & {
    opacity: 1;
  }
}
</style>

<style lang="scss" scoped>
.group {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;

  .group {
    margin: 1.5rem 0 1.5rem 1.5rem;
  }

  > .group-content > div > .group-groups > li {
    position: relative;

    &::before,
    &::after {
      content: '';

      position: absolute;
      left: 0;

      background-color: $alt-100;
    }

    &:not(:last-child) {
      &::before {
        top: -1.5rem;
        bottom: -1.5rem;

        width: 1px;
      }
    }

    &:last-child {
      &::before {
        top: -1.5rem;

        width: 1px;
        height: calc(28px + 1.5rem);
      }
    }

    &::after {
      top: 28px;

      width: 1.5rem;
      height: 1px;
    }
  }
}

.group-row--header {
  padding: 0 1rem;
  height: 56px;

  background-color: rgba($alt-50, 0.75);
}

.group-footer {
  margin-top: -1px;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;

  font-size: rem(14px);
  text-align: right;

  background-color: rgba($alt-50, 0.35);
  border: 1px solid $alt-100;
}
</style>
